import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiQuery } from '../../api/hooks';
import type { ClinicResponse } from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { FeatureFlagsSection } from './PlatformClinicsPage';
import { useLocalisedName } from '../../i18n/localisedName';

/**
 * Feature flags, reached by picking a clinic first (FR-108).
 *
 * Flags are per-clinic, so a screen that showed them without one would have nothing to show. The
 * same table is reachable inline from the clinic list; this route exists because the navigation
 * promises it, and because "switch something on for a clinic" is a task an operator arrives with
 * rather than something they stumble into while reading a tenant list.
 */
export function PlatformFeatureFlagsPage() {
  const localised = useLocalisedName();
  const { t } = useTranslation();
  const [clinicId, setClinicId] = useState('');

  const clinics = useApiQuery<ClinicResponse[]>(queryKeys.platformClinics(), '/platform/clinics');

  return (
    <>
      <PageHeader title={t('nav.items.featureFlags')} description={t('platform.flagsDescription')} />

      <AsyncSection
        query={clinics}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('platform.emptyTitle')}
        emptyDescription={t('platform.emptyDescription')}
      >
        {(rows) => (
          <div className="toolbar">
            <label className="field">
              <span className="field__label">{t('platform.clinic')}</span>
              <select
                className="field__input"
                value={clinicId}
                onChange={(event) => setClinicId(event.target.value)}
              >
                <option value="">{t('platform.chooseClinic')}</option>
                {rows.map((clinic) => (
                  <option key={clinic.id} value={clinic.id}>
                    {localised.displayName(clinic, clinic.legalName)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </AsyncSection>

      {clinicId ? <FeatureFlagsSection clinicId={clinicId} /> : null}
    </>
  );
}
